import { Injectable } from '@angular/core';
import
{
    IChatRepositoryItem,
    IChatRepositoryMessage,
    IChatRepositorySetting, IChatRepositorySettingItem
} from "@core/office/office-chat-repository/office-chat-repository.types";
import { environment } from "@env";
import { logger } from "@services/logger/logger.service";
import { DBConfig, NgxIndexedDBService } from 'ngx-indexed-db';
import { firstValueFrom } from "rxjs";

const DB = environment.db;
export const SCHEMA_NAME_CHAT_MESSAGE = 'chatHistory';
export const SCHEMA_NAME_CHAT_SETTING = 'chatSetting';

const MESSAGE_RETENTION_DAYS = 30;
const MESSAGE_RETENTION_MS = MESSAGE_RETENTION_DAYS * 24 * 60 * 60 * 1000;

export const dbConfig: DBConfig = {
    name: DB.name,
    version: DB.version,
    objectStoresMeta: [
        {
            store: SCHEMA_NAME_CHAT_MESSAGE,
            storeConfig: { keyPath: 'id', autoIncrement: true },
            storeSchema: [
                { name: 'id', keypath: 'id', options: { unique: true } },
                { name: 'version', keypath: 'version', options: { unique: false } },
                { name: 'history', keypath: 'history', options: { unique: false, multiEntry: true } }
            ]
        },
        {
            store: SCHEMA_NAME_CHAT_SETTING,
            storeConfig: { keyPath: 'user', autoIncrement: true },
            storeSchema: [
                { name: 'user', keypath: 'user', options: { unique: true } },
                { name: 'systemMessage', keypath: 'systemMessage', options: { unique: false } },
                { name: 'options', keypath: 'options', options: { unique: false, multiEntry: true } }
            ]
        }
    ]
};


@Injectable({
    providedIn: 'root'
})
export class OfficeChatRepository
{
    private userID: string = '';
    private documentID: string = 'web';
    private historyKeySeparator = '--@@--';
    private settingKey = '';

    constructor(private db: NgxIndexedDBService)
    {
    }

    public defineHistoryKey(userID: string, documentID: string = this.documentID): string
    {
        this.userID = userID;
        this.documentID = documentID;
        return this.getHistoryKey();
    }

    public getHistoryKey(): string
    {
        if (!this.userID || !this.documentID)
        {
            throw new Error('Can not get history key,please check your value');
        }
        return `${ this.userID }${ this.historyKeySeparator }${ this.documentID }`;
    }

    public defineSettingKey(userID: string): void
    {
        this.settingKey = userID;
    }

    public getSettingKey(): string
    {
        if (!this.settingKey)
        {
            throw new Error('Can not get setting key,please check your value');
        }
        return this.settingKey;
    }

    private async readHistoryItem(): Promise<IChatRepositoryItem | undefined>
    {
        const key = this.getHistoryKey();
        const dataObserver = this.db.getByKey<IChatRepositoryItem>(SCHEMA_NAME_CHAT_MESSAGE, key);
        return firstValueFrom<IChatRepositoryItem>(dataObserver);
    }

    public async readMessages(): Promise<IChatRepositoryMessage[]>
    {
        const data = await this.readHistoryItem();
        if (data && data.history)
        {
            return this.pruneExpiredMessages(
                data.history,
                data.historyVersion ?? null,
                data.threadVersion ?? null,
                data.threadId ?? null,
            );
        }

        return [];
    }

    public async readHistoryVersion(): Promise<number | null>
    {
        const data = await this.readHistoryItem();
        return data?.historyVersion ?? null;
    }

    public async readThreadVersionAndThreadId(): Promise<{ threadVersion: number | null; threadId: string | null; }>
    {
        const data = await this.readHistoryItem();
        return {
            threadVersion: data?.threadVersion ?? null,
            threadId: data?.threadId ?? null,
        };
    }

    private async pruneExpiredMessages(
        history: IChatRepositoryMessage[],
        historyVersion: number | null,
        threadVersion: number | null,
        threadId: string | null,
    ): Promise<IChatRepositoryMessage[]>
    {
        const now = Date.now();
        const cutoff = now - MESSAGE_RETENTION_MS;
        const kept: IChatRepositoryMessage[] = [];
        let mutated = false;

        for (const message of history)
        {
            if (message.createdAt == null)
            {
                // Legacy message without a timestamp: stamp it as "now" so it ages from this point forward
                // instead of being deleted immediately.
                message.createdAt = now;
                mutated = true;
            }

            if (message.createdAt >= cutoff)
            {
                kept.push(message);
            }
            else
            {
                mutated = true;
            }
        }

        if (mutated)
        {
            try
            {
                const data = {
                    version: DB.version,
                    historyVersion,
                    threadVersion,
                    id: this.getHistoryKey(),
                    history: kept,
                    threadId,
                };
                await firstValueFrom(this.db.update<IChatRepositoryItem>(SCHEMA_NAME_CHAT_MESSAGE, data));
            }
            catch (e)
            {
                logger.error(e);
            }
        }

        return kept;
    }

    public async writeMessages(
        messages: IChatRepositoryMessage[],
        historyVersion?: number,
        threadId?: string | null
    ): Promise<IChatRepositoryMessage[]>
    {
        const current = await this.readHistoryItem();
        const data = {
            version: DB.version,
            historyVersion: historyVersion ?? current?.historyVersion ?? null,
            threadVersion: current?.threadVersion ?? null,
            id: this.getHistoryKey(),
            history: messages,
            threadId: threadId ?? current?.threadId ?? null,
        };

        try
        {
            const dataObserver = this.db.update<IChatRepositoryItem>(SCHEMA_NAME_CHAT_MESSAGE, data);
            await firstValueFrom(dataObserver);
        } catch (e)
        {
            logger.error(e);
        }
        return messages;
    }

    public async appendMessage(message: IChatRepositoryMessage): Promise<IChatRepositoryMessage[]>
    {
        try
        {
            const history = await this.readMessages();
            return await this.writeMessages([ ...history, message ]);
        } catch (e)
        {
            logger.error(e);
            return this.writeMessages([ message ]);
        }
    }

    public async deleteMessage(index: number): Promise<void>
    {
        const key = this.getHistoryKey();

        try
        {
            const dataObserver = this.db.getByKey<IChatRepositoryItem>(SCHEMA_NAME_CHAT_MESSAGE, key);
            const data = await firstValueFrom<IChatRepositoryItem>(dataObserver);
            const history = data?.history ?? [];

            if (index < 0 || index >= history.length)
            {
                return;
            }

            await firstValueFrom(this.db.update<IChatRepositoryItem>(SCHEMA_NAME_CHAT_MESSAGE, {
                version: DB.version,
                historyVersion: data?.historyVersion ?? null,
                threadVersion: data?.threadVersion ?? null,
                id: key,
                history: [ ...history.slice(0, index), ...history.slice(index + 1) ],
                threadId: data?.threadId,
            }));
        } catch (e)
        {
            logger.error(e);
        }
    }

    public async deleteMessages(): Promise<void>
    {
        const key = this.getHistoryKey();
        this.db.deleteByKey(SCHEMA_NAME_CHAT_MESSAGE, key).subscribe(logger.log);
    }

    public async readThreadId(): Promise<string | null>
    {
        const data = await this.readHistoryItem();
        return data?.threadId ?? null;
    }

    public async writeThreadId(threadId: string | null): Promise<void>
    {
        const key = this.getHistoryKey();
        const dataObserver = this.db.getByKey<IChatRepositoryItem>(SCHEMA_NAME_CHAT_MESSAGE, key);
        const data = await firstValueFrom<IChatRepositoryItem>(dataObserver);

        await firstValueFrom(this.db.update<IChatRepositoryItem>(SCHEMA_NAME_CHAT_MESSAGE, {
            version: DB.version,
            historyVersion: data?.historyVersion ?? null,
            threadVersion: data?.threadVersion ?? null,
            id: key,
            history: data?.history ?? [],
            threadId,
        }));
    }

    public async writeThreadVersion(threadVersion: number): Promise<void>
    {
        const key = this.getHistoryKey();
        const dataObserver = this.db.getByKey<IChatRepositoryItem>(SCHEMA_NAME_CHAT_MESSAGE, key);
        const data = await firstValueFrom<IChatRepositoryItem>(dataObserver);

        await firstValueFrom(this.db.update<IChatRepositoryItem>(SCHEMA_NAME_CHAT_MESSAGE, {
            version: DB.version,
            historyVersion: data?.historyVersion ?? null,
            threadVersion,
            id: key,
            history: data?.history ?? [],
            threadId: data?.threadId ?? null,
        }));
    }

    public async deleteSetting(): Promise<void>
    {
        const key = this.getSettingKey();
        this.db.deleteByKey(SCHEMA_NAME_CHAT_SETTING, key).subscribe(logger.log);
    }

    public async writeSetting(setting: IChatRepositorySetting): Promise<void>
    {
        const data = {
            ...setting,
            version: DB.version,
            user: this.getSettingKey(),
        };

        try
        {
            const dataObserver = this.db.update<IChatRepositorySettingItem>(SCHEMA_NAME_CHAT_SETTING, data);
            await firstValueFrom(dataObserver);
        } catch (e)
        {
            logger.error(e);
        }
    }

    public async readSetting(): Promise<IChatRepositorySetting | null>
    {
        const key = this.getSettingKey();
        const dataObserver = this.db.getByKey<IChatRepositorySettingItem>(SCHEMA_NAME_CHAT_SETTING, key);
        const data = await firstValueFrom<IChatRepositorySettingItem>(dataObserver);
        if (data)
        {
            return {
                systemMessage: data.systemMessage,
                options: data.options,
                quickActions: data.quickActions,
                skills: data.skills ?? [],
                webSearch: data.webSearch ?? false,
                imageModel: data.imageModel,
                copilotId: data.copilotId,
            };
        }

        return null;
    }
}
